<script setup lang="ts">
/**
 * A target's fields.
 *
 * Laid out as the target reads: where it started, where it stands, where it is
 * going. The progress is nowhere here, because it is not a field: it is worked
 * out from these three and shown on the page.
 */
import { useI18n } from 'vue-i18n'

import type { TargetSection } from '@/domain/section'
import InspectorField from '@/sections/InspectorField.vue'

const { t } = useI18n()

const props = defineProps<{ section: TargetSection }>()
const emit = defineEmits<{ change: [section: TargetSection] }>()

function set(changes: Partial<TargetSection>): void {
  emit('change', { ...props.section, ...changes })
}
</script>

<template>
  <div class="fields">
    <InspectorField :label="t('inspector.target.label')">
      <input
        class="b-input"
        :value="section.label"
        :placeholder="t('inspector.target.labelPlaceholder')"
        @input="set({ label: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>

    <InspectorField :label="t('inspector.target.unit')">
      <input
        class="b-input"
        :value="section.unit"
        :placeholder="t('inspector.target.unitPlaceholder')"
        @input="set({ unit: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>

    <div class="pair">
      <InspectorField :label="t('inspector.target.baseValue')">
        <input
          class="b-input"
          :value="section.baseValue"
          @input="set({ baseValue: ($event.target as HTMLInputElement).value })"
        />
      </InspectorField>

      <InspectorField :label="t('inspector.target.baseYear')">
        <input
          class="b-input"
          :value="section.baseYear"
          placeholder="2019"
          @input="set({ baseYear: ($event.target as HTMLInputElement).value })"
        />
      </InspectorField>
    </div>

    <div class="pair">
      <InspectorField :label="t('inspector.target.targetValue')">
        <input
          class="b-input"
          :value="section.targetValue"
          @input="set({ targetValue: ($event.target as HTMLInputElement).value })"
        />
      </InspectorField>

      <InspectorField :label="t('inspector.target.targetYear')">
        <input
          class="b-input"
          :value="section.targetYear"
          placeholder="2030"
          @input="set({ targetYear: ($event.target as HTMLInputElement).value })"
        />
      </InspectorField>
    </div>

    <InspectorField
      :label="t('inspector.target.currentValue')"
      :hint="t('inspector.target.currentHint')"
    >
      <input
        class="b-input"
        :value="section.currentValue"
        @input="set({ currentValue: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>

    <InspectorField :label="t('inspector.target.note')" :hint="t('inspector.target.noteHint')">
      <textarea
        class="b-textarea"
        :value="section.note"
        @input="set({ note: ($event.target as HTMLTextAreaElement).value })"
      />
    </InspectorField>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.pair {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: var(--sp-4);
}
</style>
